/* eslint-disable no-param-reassign */
import React from "react";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { Modal, message, Divider } from "antd";
import rtkRequestStatusCodes from "../../lib/const/rtkRequestStatusCodes";
import {
  getProcessBatchRoute,
  getStatementRoute,
  getStatementsRoute,
  updateLineRoute,
  getUploadBatchRoute,
  getLineRoute,
} from "../../lib/routes/payments-loader";
import warningIcon from "../../img/warning";

const initialState = {
  statements: [],
  statementsLoading: rtkRequestStatusCodes.idle,
  statement: {},
  statementLoading: rtkRequestStatusCodes.idle,
  totalElements: 0,
  currentPage: 1,
};

const fetchStatements = createAsyncThunk("statements/fetchAll", async (searchParams) => {
  const response = axios.get(getStatementsRoute({ ...searchParams, size: 10 }));
  const { data } = await response;
  return data;
});

const fetchStatement = createAsyncThunk(
  "statements/fetchStatement",
  async ({ statementId, addedAccounts = {} }) => {
    const url = getStatementRoute(statementId, addedAccounts);
    const response = axios.get(url);
    const { data } = await response;
    return data;
  }
);

const fetchLine = createAsyncThunk(
  "statements/fetchLine",
  async ({ lineId, addedAccounts = [] }) => {
    const url = getLineRoute({ lineId, addedAccounts });
    const response = axios.get(url);
    const { data } = await response;
    return data;
  }
);

const getWarningModal = (msg) => {
  Modal.error({
    okButtonProps: {
      style: {
        marginLeft: "auto",
        marginRight: "auto",
        width: "108px",
      },
    },
    content: (
      <>
        <div
          style={{
            display: "flex",
            height: "100px",
            margin: "15px auto",
            width: "550px",
          }}
        >
          <div style={{ marginTop: "5px" }}>{warningIcon}</div>
          <div
            style={{
              marginLeft: "24px",
              fontSize: "16px",
              width: "506px",
            }}
          >
            {msg}
          </div>
        </div>
        <Divider style={{ margin: 0 }} />
      </>
    ),
    icon: null,
    width: 600,
  });
};

const updateLine = createAsyncThunk(
  "statements/updateLine",
  async ({ lineId, updatedAccountHolders }) => {
    try {
      const response = axios.put(updateLineRoute(lineId), {
        accountHolders: updatedAccountHolders,
      });
      const { data } = await response;
      return data;
    } catch (e) {
      if (e.response) {
        const { exception } = e.response.data;
        if (exception.includes("BillAmountException")) {
          getWarningModal(e.response.data.message);
        }
      }
      throw Error;
    }
  }
);

const updateLineAgreement = createAsyncThunk(
  "statements/updateLineAgreement",
  async ({ lineId, agreementId }) => {
    const response = axios.patch(updateLineRoute(lineId), {
      agreementId,
    });
    const { data } = await response;
    return data;
  }
);

const uploadBatch = createAsyncThunk(
  "statements/uploadBatch",
  async ({ currencyId, batchDate, file }) => {
    const url = getUploadBatchRoute(currencyId, batchDate);
    message.loading("Загрузка выписки...", 0);
    try {
      const { data } = await axios.post(url, file, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      message.destroy();
      message.success("Выписка загружена", 1);
      return data;
    } catch (e) {
      message.destroy();
      Modal.warning({ content: e?.response?.data?.message || "Ошибка загрузки выписки" });
      throw Error;
    }
  }
);

const processBatch = createAsyncThunk("statements/processBatch", async (batchId) => {
  const url = getProcessBatchRoute(batchId);
  try {
    const { data } = await axios.post(url);
    message.success("Выписка отправлена в обработку");
    return data;
  } catch (e) {
    Modal.error({
      content: "Ошибка обработки выписки",
    });
    throw new Error(e);
  }
});

// С бэка всегда приходит порядок следования договоров в разнобой, что не удобно
const sortAgreementHolders = (batch) => ({
  ...batch,
  lineHolders: batch.lineHolders.map((lineHolder) => ({
    ...lineHolder,
    accountHolders: lineHolder.accountHolders.map((accountHolder) => ({
      ...accountHolder,
      agreementHolders: [...accountHolder.agreementHolders].sort((a, b) =>
        a.agreement.agreementNumber > b.agreement.agreementNumber ? 1 : -1
      ),
    })),
  })),
});

const statementsSlice = createSlice({
  name: "statements",
  initialState,
  reducers: {
    resetStatement: {
      reducer: (state) => {
        state.statement = {};
        state.statementLoading = rtkRequestStatusCodes.idle;
      },
    },
  },
  extraReducers: {
    [fetchStatements.pending]: (state) => {
      state.statementsLoading = rtkRequestStatusCodes.pending;
    },
    [fetchStatements.fulfilled]: (state, action) => {
      // eslint-disable-next-line no-underscore-dangle
      state.statements = action.payload.content;
      state.totalElements = action.payload.totalElements;
      state.currentPage = action.payload.number + 1;
      state.statementsLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchStatements.rejected]: (state) => {
      state.statementsLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchStatement.pending]: (state) => {
      state.statementLoading = rtkRequestStatusCodes.pending;
    },
    [fetchStatement.fulfilled]: (state, action) => {
      // eslint-disable-next-line prefer-destructuring
      state.statement = sortAgreementHolders(action.payload.content[0]);
      state.statementLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchStatement.rejected]: (state) => {
      state.statementLoading = rtkRequestStatusCodes.rejected;
    },
    [uploadBatch.pending]: (state) => {
      state.statementLoading = rtkRequestStatusCodes.pending;
    },
    [uploadBatch.fulfilled]: (state, action) => {
      // eslint-disable-next-line prefer-destructuring
      state.statement = action.payload.content[0];
      state.statementLoading = rtkRequestStatusCodes.fulfilled;
    },
    [uploadBatch.rejected]: (state) => {
      state.statementLoading = rtkRequestStatusCodes.rejected;
    },
  },
});

const { reducer, actions } = statementsSlice;
const { resetStatement } = actions;

export {
  reducer,
  fetchStatements,
  fetchStatement,
  updateLine,
  processBatch,
  uploadBatch,
  resetStatement,
  updateLineAgreement,
  fetchLine,
};
